import React, { useCallback, useEffect, useState } from 'react';
import { useLoggedInUser, useFeatureFlags } from '@tcc/shared-data-lib';
import { useHistory, useLocation } from 'react-router-dom';
import lift from '@tcc/load-lifter';
import { getMessage } from '@tcc/i18n';
import { FdsIconType } from '@tcc/fds';
import { importFromS3 } from '../../utils/importFromS3';
import { useUserLoggedInConfigs, useFeatureFlagsConfigs } from '../../utils/sharedData';
import { useNavigationItems } from '../../hooks/useNavigationLinks/useNavigationItems';
import { useNavFooterItems } from '../../hooks/useNavFooterItems';
import { Operation } from '../../hooks/useOperations/types';
import { shouldShowOperationSelector } from '../../utils/shouldShowOperationSelector';
import { shouldShowMultiOperationSelector } from '../../utils/shouldShowMultiOperationSelector';
import { toggleOperationSelectionMode } from '../../utils/toggleOperationSelectionMode';
import { getOperationSelectorDisplayText } from '../../utils/getOperationSelectorDisplayText';
import { HermesLibrary } from '../../types/Hermes';
import { useUserSubMenu } from '../../hooks/useUserSubMenu';
import { NavigationItem } from '../../hooks/useNavigationLinks/types';

type UserInfoProps = {
  email: string;
  fullName: string;
  avatar: string;
};

type SubMenuItemProps = {
  label: string;
  leadingIcon?: FdsIconType;
  dataTestId?: string;
  onClick: () => void;
};

type FooterItem = {
  label: string;
  href?: string;
  icon?: React.ReactElement;
  testId?: string;
  target?: string;
  onClick?: () => void;
};

type Props = {
  allOperationsDisplayMessage: string
  areNotificationsLoading: boolean;
  expanded: boolean;
  hasMultiOperationEnabled: boolean;
  hasOperationDropdown: boolean;
  navigationItems?: NavigationItem[];
  navFooterItems?: FooterItem[];
  onLogoClick?: () => void;
  operations: Operation[];
  operationsSelected: Operation[];
  operationsSelectedDisplayMessage: string;
  setExpandedState: (isExpanded: boolean) => void;
  setSelectedOperations: (operationSelected: Operation[]) => void;
  subMenusUserProfile: SubMenuItemProps[];
  userInfo: UserInfoProps;
};

interface IProps {
  expanded: boolean;
  operations: Operation[];
  operationsSelected: Operation[];
  setExpandedState: (isExpanded: boolean) => void;
  setSelectedOperations: (operationSelected: Operation[]) => void;
}

const InformationArchitecturePlugin: React.FC<Props> = React.lazy(() =>
  importFromS3('information-architecture')
);

export const InfoArchitecturePluginContainer: React.FC<IProps> = ({
  expanded,
  operations,
  operationsSelected,
  setExpandedState,
  setSelectedOperations,
}: IProps) => {
  const { navigationItems } = useNavigationItems();
  const { user, user: { details } } = useLoggedInUser(useUserLoggedInConfigs);
  const { featureFlags } = useFeatureFlags(useFeatureFlagsConfigs(user));
  const [areNotificationsLoading, setNotificationsLoading] = useState(true);
  const history = useHistory();
  const location = useLocation();


  useEffect(() => {
    if (user && operationsSelected.length > 0) {
      lift(['tcc-hermes'], (Hermes: HermesLibrary) => {
        setNotificationsLoading(false);
        Hermes.install(
          null,
          {
            containerSelector: '#hermesButton',
            insideDashboard: false,
          },
          user.details,
          operationsSelected[0].operationId
        );
      });
    }
  }, [user, operationsSelected]);

  const userInfo = {
    fullName: `${details?.firstName} ${details?.lastName}`,
    email: details?.email,
    avatar: `${details?.firstName?.charAt(0)}${details?.lastName?.charAt(0)}`,
  };

  const hasOperationDropdown = shouldShowOperationSelector({ user, route: location.pathname }) && operations.length !== 0;

  const hasMultiOperationEnabled = shouldShowMultiOperationSelector(location.pathname, featureFlags);

  toggleOperationSelectionMode({
    hasMultiOperationEnabled,
    operationsSelected,
    operations,
    setSelectedOperations
  });

  const operationsSelectedDisplayMessage = getOperationSelectorDisplayText({
    operations,
    operationsSelected
  });

  const subMenus = useUserSubMenu({ featureFlags, user });

  const navFooterItems = useNavFooterItems({
    expanded,
    setExpandedState,
    countryCode: details?.countryCode,
    featureFlags,
  });

  const onLogoClick = useCallback(() => {
    history.push(`/${location.search}`);
  }, [history, location]);

  return (
    <InformationArchitecturePlugin
      allOperationsDisplayMessage={getMessage('sidenav.allOperations')}
      areNotificationsLoading={areNotificationsLoading}
      expanded={expanded}
      hasOperationDropdown={hasOperationDropdown}
      hasMultiOperationEnabled={hasMultiOperationEnabled}
      navigationItems={navigationItems}
      navFooterItems={navFooterItems}
      onLogoClick={onLogoClick}
      operations={operations}
      operationsSelected={operationsSelected}
      operationsSelectedDisplayMessage={operationsSelectedDisplayMessage}
      setExpandedState={setExpandedState}
      setSelectedOperations={setSelectedOperations}
      subMenusUserProfile={subMenus}
      userInfo={userInfo}
    />
  );
};
